const UINT64_MAX = 18_446_744_073_709_551_615n

/**
 * OpenProject RPC 可以直接消费的请求入参。
 */
export interface OpenProjectRequestInput {
  /** 项目 ID，对应 `OpenProjectRequest.projectId`。 */
  readonly projectId: bigint
  /** 目标仓库 ID；缺省时由后端使用项目根仓库。 */
  readonly repositoryId?: bigint
  /** 目标分支 ID；缺省或为 0 时由后端使用仓库默认分支。 */
  readonly branchId?: bigint
  /** 目标提交 ID；缺省时由后端按分支 HEAD 打开。 */
  readonly commitId?: string
}

/**
 * 页面打开项目时的完整上下文。
 */
export interface OpenProjectSessionInput extends OpenProjectRequestInput {
  /** 路由中的项目 ID 原文，用作本地缓存、偏好和日志的稳定 key。 */
  readonly projectIdText: string
  /** 路由 query 中的仓库 ID 原文；缺省时不写入 URL。 */
  readonly repositoryIdText?: string
  /** 路由 query 中的分支 ID 原文；缺省时不写入 URL。 */
  readonly branchIdText?: string
  /** 整项目历史版本切换时使用的目标提交 ID 原文。 */
  readonly commitIdText?: string
}

/**
 * 兼容仍只传项目 ID 字符串的旧调用点。
 */
export type OpenProjectSessionSource = OpenProjectSessionInput | string

/**
 * 将 OpenProject 使用的 ID 字符串解析为 uint64 bigint。
 *
 * 设计说明：
 * 1. 当前 proto 已将项目、仓库、分支 ID 定义为 uint64，前端不能继续把任意路径字符串透传给后端；
 * 2. 在 API 边界集中校验可以避免 Vue 页面里散落 bigint 转换，也能让错误信息带上明确字段名；
 * 3. branchId=0 是协议中的有效语义，因此只把空字符串视为缺省，不把 0 过滤掉。
 *
 * @param rawId URL 或环境变量中读取到的 ID 原文。
 * @param fieldName 错误信息中使用的字段名。
 * @returns 可直接传给 proto request 的 uint64 bigint。
 */
function parseOpenProjectUint64Id(rawId: string, fieldName: string): bigint {
  const normalizedId = rawId.trim()
  if (!normalizedId) {
    throw new Error(`OpenProject 路由参数无效：${fieldName} 不能为空`)
  }
  if (!/^\d+$/u.test(normalizedId)) {
    throw new Error(`OpenProject 路由参数无效：${fieldName} 必须是 uint64 数字，当前值=${rawId}`)
  }

  const parsedId = BigInt(normalizedId)
  if (parsedId > UINT64_MAX) {
    throw new Error(`OpenProject 路由参数无效：${fieldName} 超出 uint64 范围，当前值=${rawId}`)
  }

  return parsedId
}

/**
 * 解析可选 uint64 ID。
 *
 * @param rawId URL 或环境变量中读取到的可选 ID 原文。
 * @param fieldName 错误信息中使用的字段名。
 * @returns 缺省时返回 undefined，否则返回 uint64 bigint。
 */
function parseOptionalOpenProjectUint64Id(
  rawId: string | null | undefined,
  fieldName: string,
): bigint | undefined {
  const normalizedId = rawId?.trim() ?? ''
  if (!normalizedId) {
    return undefined
  }
  return parseOpenProjectUint64Id(normalizedId, fieldName)
}

function normalizeOptionalOpenProjectCommitId(rawId: string | null | undefined): string | undefined {
  const normalizedId = rawId?.trim() ?? ''
  return normalizedId || undefined
}

/**
 * 从 URL 片段创建 OpenProject 会话上下文。
 *
 * @param input 路由 params/query 中的项目、仓库和分支 ID。
 * @returns 同时保留文本 key 与 proto bigint 入参的上下文。
 */
export function createOpenProjectSessionInput(input: {
  readonly projectId: string
  readonly repositoryId?: string | null
  readonly branchId?: string | null
  readonly commitId?: string | null
}): OpenProjectSessionInput {
  const projectIdText = input.projectId.trim()
  const repositoryIdText = input.repositoryId?.trim() || undefined
  const branchIdText = input.branchId?.trim() || undefined
  const commitIdText = normalizeOptionalOpenProjectCommitId(input.commitId)

  return {
    projectIdText,
    repositoryIdText,
    branchIdText,
    commitIdText,
    projectId: parseOpenProjectUint64Id(projectIdText, 'projectId'),
    repositoryId: parseOptionalOpenProjectUint64Id(repositoryIdText, 'repositoryId'),
    branchId: parseOptionalOpenProjectUint64Id(branchIdText, 'branchId'),
    commitId: commitIdText,
  }
}

/**
 * 将旧字符串入参规范化为完整 OpenProject 会话上下文。
 *
 * @param source 旧项目 ID 字符串或已经解析好的会话上下文。
 * @returns 完整会话上下文。
 */
export function normalizeOpenProjectSessionSource(
  source: OpenProjectSessionSource,
): OpenProjectSessionInput {
  if (typeof source === 'string') {
    return createOpenProjectSessionInput({ projectId: source })
  }
  return source
}
